// src/views/SupportPage.js
// VERIFIER: SUPPORT-MIGRATION-FAQ-v1 — added during 30-day-trial migration session
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  LifeBuoy,
  Mail,
  CheckCircle,
  Send,
  AlertCircle,
  Loader2,
  Shield,
} from 'lucide-react';

const SupportPage = () => {
  const supportEmail = 'support@spatchy.co';

  // Email copy-to-clipboard
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(supportEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    botcheck: '', // Honeypot field for spam — must stay empty
  });
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot — if filled, silently "succeed" without sending (it's a bot)
    if (formData.botcheck) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          name: formData.name,
          email: formData.email,
          subject: formData.subject || 'Support request from spatchy.co/support',
          message: formData.message,
          from_name: 'Spatchy Support Form',
          replyto: formData.email,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', botcheck: '' });
      } else {
        setStatus('error');
        setErrorMessage(
          result.message || 'Something went wrong. Please email us directly at support@spatchy.co.'
        );
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        'Could not send your message. Please email us directly at support@spatchy.co.'
      );
    }
  };

  // FAQ content
  const faqs = [
    {
      question: 'How do I get started with Spatchy?',
      answer:
        "Sign up at app.spatchy.co/pricing — pick a plan and start your 30-day free trial. No credit card needed up front. From there, you'll be guided through setting up your account, importing your team, and adding your first customers. Coming from another tool like Jobber or a stack of spreadsheets? Every new customer gets free white-glove migration — see the next FAQ for details. Need a hand at any point? Email us at support@spatchy.co.",
    },
    {
      question: 'Can you help me migrate my data from another tool?',
      answer:
        "Yes — every new customer gets free white-glove migration through 2026. On a 60-minute call (often with me, the founder), we'll personally move your customers, jobs, schedules, and contacts into Spatchy — whether you're coming from Jobber, Service Autopilot, Housecall Pro, or a stack of spreadsheets. No double data entry, no import wizards. Fill out the migration form on our homepage at spatchy.co to get started.",
    },
    {
      question: 'How does billing work?',
      answer:
        "Spatchy is subscription-based with monthly or annual options. Pricing is in CAD for Canadian customers and USD for U.S. customers, automatically detected at signup. Every plan includes every feature — no upgrade tiers, no per-seat fees. You'll be billed automatically at the end of your free trial unless you cancel beforehand.",
    },
    {
      question: 'How do I cancel my subscription?',
      answer:
        "Log in to your account, go to Settings → Billing, and click Cancel Subscription. Your access continues until the end of your current paid period — no surprise charges. You can also email support@spatchy.co if you'd prefer help from us directly.",
    },
    {
      question: 'How do I delete my account?',
      answer:
        "You can delete your account from inside the app: go to Settings → Account → Delete Account. If you're the billing owner of an organization, you'll be guided through deleting the entire org including all team members and data. Deleted organizations have a 90-day grace period during which you can reinstate them — after 90 days, all data is permanently removed. You can also visit spatchy.co/delete-account for instructions.",
    },
    {
      question: 'Is my data secure?',
      answer:
        'Yes. All data is encrypted in transit (TLS) and at rest. Passwords are hashed using industry-standard algorithms — we never see them. Payment information is handled entirely by Stripe (PCI-compliant). Sensitive integration credentials like QuickBooks tokens are encrypted with AES-256. We host on Google Cloud infrastructure (SOC 2 and ISO 27001 certified). For more details, see our Privacy Policy.',
    },
    {
      question: 'How do I request my personal data, or have it deleted?',
      answer:
        "You have the right to request access to, correction of, or deletion of your personal information. The fastest way to delete your data is using the in-app account deletion flow described above. For other requests — like a data export, correction, or formal data subject access request — use the contact form on this page or email support@spatchy.co. We'll respond within 30 days.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Helmet>
        <title>Support | Spatchy</title>
        <meta
          name="description"
          content="Get help with Spatchy. Contact our support team, find answers to common questions, or submit a data privacy request."
        />
      </Helmet>

      {/* Header */}
      <div className="border-b border-white/10 bg-slate-900/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <LifeBuoy size={24} className="text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Support</h1>
          </div>
          <p className="text-slate-400 mt-2 text-sm">
            We're a small team and we read every message. Real help, real fast.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">

        {/* Quick contact */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-3">Get in touch</h2>
          <p className="text-slate-400 leading-relaxed mb-6">
            The fastest way to reach us is email. Drop us a line and we'll get back to you within{' '}
            <strong className="text-white">one business day</strong> — usually much sooner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={`mailto:${supportEmail}`}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors"
            >
              <Mail size={18} />
              Email {supportEmail}
            </a>
            <button
              onClick={handleCopy}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors border border-white/10"
            >
              {copied ? <CheckCircle size={18} className="text-green-400" /> : <Mail size={18} />}
              {copied ? 'Copied!' : `Copy email`}
            </button>
          </div>
        </div>

        {/* Contact form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-3">Send us a message</h2>
          <p className="text-slate-400 leading-relaxed mb-6">
            Prefer to use a form? Fill this out and we'll reply by email.
          </p>

          {status === 'success' ? (
            <div className="flex items-start gap-3 bg-green-500/10 border border-green-500/30 rounded-xl p-5">
              <CheckCircle size={22} className="text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-semibold mb-1">Message sent — thank you!</p>
                <p className="text-slate-300 text-sm">
                  We've got your message and we'll be in touch by email shortly.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot — visually hidden, screen-reader hidden, must stay empty */}
              <input
                type="text"
                name="botcheck"
                value={formData.botcheck}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                    Your name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={status === 'submitting'}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={status === 'submitting'}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50"
                    placeholder="jane@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-300 mb-2">
                  Subject <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={status === 'submitting'}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={status === 'submitting'}
                  rows={6}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50 resize-vertical"
                  placeholder="Tell us how we can help..."
                />
              </div>

              {status === 'error' && (
                <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-200 text-sm">{errorMessage}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send message
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* FAQ */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className={i > 0 ? 'pt-6 border-t border-white/10' : ''}>
                <h3 className="text-base font-semibold text-white mb-2">{faq.question}</h3>
                <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy / data rights */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-3">
            <Shield size={20} className="text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Privacy & data rights</h2>
          </div>
          <p className="text-slate-400 leading-relaxed mb-4">
            You have the right to access, correct, export, or delete your personal data. The fastest way to
            delete your data is the{' '}
            <Link to="/delete-account" className="text-blue-400 hover:text-blue-300 font-medium">
              in-app account deletion flow
            </Link>
            . For all other requests — including data subject access requests under CCPA, PIPEDA, or other
            privacy laws — use the contact form above or email{' '}
            <a
              href={`mailto:${supportEmail}`}
              className="text-blue-400 hover:text-blue-300 font-medium"
            >
              {supportEmail}
            </a>
            . We respond to all privacy requests within 30 days.
          </p>
          <p className="text-slate-400 leading-relaxed text-sm">
            For full details on how we handle your data, see our{' '}
            <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 font-medium">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link to="/terms" className="text-blue-400 hover:text-blue-300 font-medium">
              Terms of Service
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;